import type {UnionToIntersection} from './function-composition-types';

type TypeGuard<A, B extends A> = (a: A) => a is B;
type GuardType<T> = T extends (o: any) => o is infer U ? U : never;

/**
 * Combinatoric functions for type guards that allows
 * for a functional approach to combining type guards
 */

/**
 * Use this function to get the _union_ of a set of type guards.
 *
 * @example
 * ```ts
 * // Type guards
 * declare function isString(x: any): x is string;
 * declare function isFoo(x: any): x is {foo: string};
 *
 * // We can "or" the type guards
 * const isStringOrFoo = Or([isString, isFoo]);
 * const isOr_value = isStringOrFoo({ foo: 'bar' }); // boolean
 * [{}].filter(isStringOrFoo).forEach(x => { }); // x is now string | {foo: string}
 * ```
 */
export function Or<T extends TypeGuard<any, any>>(
    guards: T[],
): [T] extends [TypeGuard<infer A, any>] ? (a: A) => a is GuardType<T> : never;
export function Or<T extends TypeGuard<any, any>>(guards: T[]) {
    return (arg: T) => guards.some(predicate => predicate(arg));
}

/**
 * Use this function to get the _intersection_ of a set of type guards.
 *
 * @example
 * ```ts
 * // Type guards
 * declare function isCool(x: any): x is {bar: 'cool-const'};
 * declare function isFoo(x: any): x is {foo: string};
 *
 * // We can "and" the type guards
 * const isCoolAndFoo = And([isCool, isFoo]);
 * const isAnd_value = isStringOrFoo({ foo: 'bar' }); // boolean
 * [{}].filter(isCoolAndFoo).forEach(x => { }); // x is now {bar: 'cool-const'; foo: string}
 * ```
 */
export function And<T extends TypeGuard<any, any>>(
    guards: T[],
): [T] extends [TypeGuard<infer A, any>] ? (a: A) => a is Extract<UnionToIntersection<GuardType<T>>, A> : never;
export function And<T extends TypeGuard<any, any>>(guards: T[]) {
    return (arg: T) => guards.every(predicate => predicate(arg));
}
